import * as _ from "lodash";

import { Reducer } from "@ctra/utils";

import { cleanup } from "../../utils/reducer";
import types from "./types";
import { GamePlanState } from "./typings";
import { NormalizedGamePlanList } from "./schemas";
import { CreateTaskListFulfilledPayload } from "./actions";

export const initialState: GamePlanState = {
  entities: {},
  todos: {}
};

/**
 * Game plan & task list reducer
 * @category Reducer
 * @param state
 * @param action
 */
const reducer: Reducer<GamePlanState> = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case types.CREATE_GAME_PLAN.fulfilled:
    case types.FETCH_GAME_PLAN_LIST.fulfilled: {
      const {
        entities: { gamePlan, task }
      } = payload as NormalizedGamePlanList;

      /**
       * Exclude the game plans which have no data descriptor ID
       * @todo replace this when the "no KPI" option is available for game plans
       */
      const excludedGamePlans = _.pickBy(gamePlan, ["dataDescriptorID", null]);
      const excludedGamePlanIDs = _.map(excludedGamePlans, "id");

      if (excludedGamePlanIDs.length) {
        console.error("Game plan objects with no data descriptor id", { excludedGamePlans });
      }

      /**
       * Get the task IDs which are supposed to be excluded as well
       */
      const excludedTaskIDs = _.flatMap(excludedGamePlans, "taskList");

      const includedGamePlans = _.omit(gamePlan, excludedGamePlanIDs);
      const includedTasks = _.omit(task, excludedTaskIDs);

      return {
        entities: {
          ...state.entities,
          ...includedGamePlans
        },
        todos: {
          ...state.todos,
          ...includedTasks
        }
      };
    }
    case types.UPDATE_GAME_PLAN.fulfilled: {
      const {
        entities: { gamePlan },
        result
      } = payload;

      return {
        ...state,
        entities: {
          ...state.entities,
          [result]: {
            ...state.entities[result],
            ...gamePlan[result]
          }
        }
      };
    }
    case types.DELETE_GAME_PLAN.fulfilled: {
      const { id } = payload;
      const taskList = state.entities[id].taskList;

      return {
        entities: _.omit(state.entities, id),
        todos: _.omit(state.todos, taskList)
      };
    }
    case types.CREATE_TODO_LIST.fulfilled: {
      const {
        gamePlanID,
        entities: { task },
        result
      } = payload as CreateTaskListFulfilledPayload;

      return {
        entities: {
          ...state.entities,
          [gamePlanID]: {
            ...state.entities[gamePlanID],
            taskList: _.uniq([...state.entities[gamePlanID].taskList, ...result])
          }
        },
        todos: {
          ...state.todos,
          ...task
        }
      };
    }
    case types.UPDATE_TODO_LIST.fulfilled: {
      const {
        entities: { task },
        gamePlanID,
        result
      } = payload;

      const gamePlan = state.entities[gamePlanID];

      return {
        todos: {
          /**
           * Remove the old tasks (so the deleted ones are gone)
           */
          ..._.omit(state.todos, gamePlan.taskList),
          ...task
        },
        entities: {
          ...state.entities,
          [gamePlanID]: {
            ...state.entities[gamePlanID],
            taskList: result
          }
        }
      };
    }
    case types.UPDATE_TASK.fulfilled: {
      const { id, ...task } = payload;

      return {
        ...state,
        todos: {
          ...state.todos,
          [id]: {
            id,
            ...task
          }
        }
      };
    }
  }

  return state;
};

export default cleanup<GamePlanState>(initialState)(reducer);
